import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { CrmContact, CrmRecordType } from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import { formatPhoneAsYouType } from "@/lib/validation/phoneNumber";
import type { RecordFormValues } from "../recordFormSchema";
import { TagPicker } from "./TagPicker";

const kindOptions: { value: CrmContact["kind"]; label: string }[] = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
];

/** One row per contact: kind, value, label, tags (at least one) and delete. */
export function ContactsSection({
  rows,
  rules,
  recordType,
  notes,
  disabled,
  onAdd,
  onRemove,
  onKindChange,
  onTagsChange,
}: {
  /** useFieldArray rows; `key` is the row's stable React key. */
  rows: { key: string }[];
  rules: CrmRules;
  recordType: CrmRecordType | "";
  /** Notes about removed tags, by row key. */
  notes: Record<string, string>;
  disabled: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onKindChange: (index: number, kind: CrmContact["kind"]) => void;
  onTagsChange: (index: number, tagIds: string[]) => void;
}) {
  const form = useFormContext<RecordFormValues>();
  const contacts = form.watch("contacts");

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Contacts</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} disabled={disabled} data-testid="crm-form-add-contact">
          <Plus className="mr-2 h-4 w-4" />
          Add contact
        </Button>
      </div>
      {rows.length === 0 && <p className="text-sm text-gray-400">No contacts</p>}
      {rows.map((row, index) => {
        const kind = contacts[index]?.kind ?? "phone";
        const rowError = form.formState.errors.contacts?.[index]?.id?.message;
        return (
          <div key={row.key} className="space-y-1.5 rounded-lg border bg-gray-50/60 p-3" data-testid={`crm-form-contact-${index}`}>
            <div className="grid items-start gap-2 md:grid-cols-[120px_minmax(0,1fr)_minmax(0,180px)_minmax(0,1fr)_auto]">
              <FormField
                control={form.control}
                name={`contacts.${index}.kind`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div>
                        <Combobox
                          isModal
                          dwimOnChange
                          className="w-full"
                          virtualizeList
                          popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                          options={kindOptions}
                          value={field.value}
                          onValueChange={(value: string) => {
                            const nextKind = kindOptions.find((option) => option.value === value)?.value;
                            if (nextKind) onKindChange(index, nextKind);
                          }}
                          disabled={disabled}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`contacts.${index}.value`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        // Phones format to national format as you type: (515) 555-0100.
                        onChange={(event) =>
                          field.onChange(kind === "phone" ? formatPhoneAsYouType(event.target.value, field.value) : event.target.value)}
                        type={kind === "email" ? "email" : "tel"}
                        placeholder={kind === "email" ? "name@example.com" : "(515) 555-0100"}
                        disabled={disabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`contacts.${index}.label`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} placeholder="Label" disabled={disabled} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`contacts.${index}.tag_ids`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div>
                        {recordType ? (
                          <TagPicker
                            rules={rules}
                            target={{ type: "contact", recordType, contactKind: kind }}
                            value={field.value}
                            onChange={(tagIds: string[]) => onTagsChange(index, tagIds)}
                            disabled={disabled}
                          />
                        ) : (
                          <p className="flex h-10 items-center text-sm text-gray-400">Choose a type first</p>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(index)}
                disabled={disabled}
                aria-label="Remove contact"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {notes[row.key] && <p className="text-xs text-amber-700">{notes[row.key]}</p>}
            {rowError && <p className="text-sm font-medium text-destructive" aria-invalid>{rowError}</p>}
          </div>
        );
      })}
    </section>
  );
}
